import { useState, useEffect } from "react";
import axios from "axios";
import { 
  TrendingUp, 
  TrendingDown,
  Calendar,
  BarChart3,
  ArrowRight,
  Minus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, LineChart, Line, AreaChart, Area 
} from "recharts";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [periodType, setPeriodType] = useState("month");
  const [year, setYear] = useState(new Date().getFullYear());
  
  // Comparison settings
  const [period1, setPeriod1] = useState(1);
  const [year1, setYear1] = useState(new Date().getFullYear());
  const [period2, setPeriod2] = useState(2);
  const [year2, setYear2] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchTrends();
  }, [periodType, year]);

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/trends?period_type=${periodType}&year=${year}`);
      setTrends(response.data);
    } catch (error) {
      console.error("Error fetching trends:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async () => {
    try {
      const response = await axios.get(
        `${API}/analytics/comparison?period_type=${periodType}&period1=${period1}&year1=${year1}&period2=${period2}&year2=${year2}`
      );
      setComparison(response.data);
    } catch (error) {
      toast.error("Failed to compare periods");
    }
  };

  const getChangeIcon = (value) => {
    if (value > 0) return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (value < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  const getChangeColor = (value) => {
    if (value > 0) return "text-emerald-600";
    if (value < 0) return "text-red-600";
    return "text-muted-foreground";
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const periods = periodType === "quarter" ? [1, 2, 3, 4] : Array.from({ length: 12 }, (_, i) => i + 1);

  if (loading && !trends) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Analytics</h1>
        <p className="text-muted-foreground mt-1">Trends and period comparisons</p>
      </div>

      <Tabs defaultValue="trends" className="space-y-6">
        <TabsList>
          <TabsTrigger value="trends" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="compare" className="gap-2">
            <Calendar className="w-4 h-4" />
            Compare Periods
          </TabsTrigger>
        </TabsList>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-6">
          {/* Controls */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">View by:</span>
                  <Select value={periodType} onValueChange={setPeriodType}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="month">Monthly</SelectItem>
                      <SelectItem value="quarter">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Year:</span>
                  <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Utilization Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Utilization Rate Trend</CardTitle>
              <CardDescription>
                {periodType === "quarter" ? "Quarterly" : "Monthly"} utilization rate for {year}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trends?.trends || []}>
                    <defs>
                      <linearGradient id="colorUtilization" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" />
                    <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip 
                      formatter={(value) => [`${value}%`, "Utilization"]}
                      contentStyle={{ 
                        backgroundColor: "hsl(var(--card))", 
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px"
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="utilization_rate" 
                      stroke="#6366F1" 
                      fillOpacity={1} 
                      fill="url(#colorUtilization)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Hours Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Hours Breakdown</CardTitle>
              <CardDescription>Fee-earning vs Non-fee earning hours</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trends?.trends || []}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "hsl(var(--card))", 
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px"
                      }}
                    />
                    <Legend />
                    <Bar dataKey="fee_earning_hours" name="Fee Earning" fill="#10B981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="non_fee_hours" name="Non-Fee Earning" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Period Summary Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Period Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th>Period</th>
                      <th>Total Hours</th>
                      <th>Fee Earning</th>
                      <th>Non-Fee</th>
                      <th>Utilization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trends?.trends?.map((period) => (
                      <tr key={`${period.label}-${period.year}`}>
                        <td className="font-medium">{period.label} {period.year}</td>
                        <td className="tabular-nums">{period.total_hours}</td>
                        <td className="tabular-nums text-emerald-600">{period.fee_earning_hours}</td>
                        <td className="tabular-nums text-amber-600">{period.non_fee_hours}</td>
                        <td>
                          <Badge 
                            variant="secondary"
                            className={period.utilization_rate >= 50 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}
                          >
                            {period.utilization_rate}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compare Tab */}
        <TabsContent value="compare" className="space-y-6">
          {/* Comparison Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Compare Two Periods</CardTitle>
              <CardDescription>Select periods to compare</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Type:</span>
                  <Select value={periodType} onValueChange={setPeriodType}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="month">Monthly</SelectItem>
                      <SelectItem value="quarter">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                  <Select value={period1.toString()} onValueChange={(v) => setPeriod1(parseInt(v))}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {periods.map(p => (
                        <SelectItem key={p} value={p.toString()}>
                          {periodType === "quarter" ? `Q${p}` : MONTHS[p - 1]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={year1.toString()} onValueChange={(v) => setYear1(parseInt(v))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <ArrowRight className="w-5 h-5 text-muted-foreground" />

                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                  <Select value={period2.toString()} onValueChange={(v) => setPeriod2(parseInt(v))}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {periods.map(p => (
                        <SelectItem key={p} value={p.toString()}>
                          {periodType === "quarter" ? `Q${p}` : MONTHS[p - 1]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={year2.toString()} onValueChange={(v) => setYear2(parseInt(v))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={fetchComparison}>Compare</Button>
              </div>
            </CardContent>
          </Card>

          {/* Comparison Results */}
          {comparison && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Period 1 */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-semibold">{comparison.period1.label}</CardTitle>
                    <CardDescription>{comparison.period1.start} to {comparison.period1.end}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Hours</span>
                        <span className="font-semibold">{comparison.period1.stats.total_hours}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fee Earning</span>
                        <span className="font-semibold text-emerald-600">{comparison.period1.stats.fee_earning_hours}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Non-Fee</span>
                        <span className="font-semibold text-amber-600">{comparison.period1.stats.non_fee_hours}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Utilization</span>
                        <Badge variant="secondary">{comparison.period1.stats.utilization_rate}%</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Consultants</span>
                        <span className="font-semibold">{comparison.period1.stats.consultants}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Projects</span>
                        <span className="font-semibold">{comparison.period1.stats.projects}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Period 2 */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-semibold">{comparison.period2.label}</CardTitle>
                    <CardDescription>{comparison.period2.start} to {comparison.period2.end}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Hours</span>
                        <span className="font-semibold">{comparison.period2.stats.total_hours}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fee Earning</span>
                        <span className="font-semibold text-emerald-600">{comparison.period2.stats.fee_earning_hours}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Non-Fee</span>
                        <span className="font-semibold text-amber-600">{comparison.period2.stats.non_fee_hours}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Utilization</span>
                        <Badge variant="secondary">{comparison.period2.stats.utilization_rate}%</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Consultants</span>
                        <span className="font-semibold">{comparison.period2.stats.consultants}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Projects</span>
                        <span className="font-semibold">{comparison.period2.stats.projects}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Changes Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Changes</CardTitle>
                  <CardDescription>{comparison.period1.label} vs {comparison.period2.label}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {Object.entries(comparison.changes).map(([key, value]) => (
                      <div key={key} className="p-4 rounded-lg bg-muted/50 text-center">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                          {key.replace(/_/g, " ")}
                        </p>
                        <div className="flex items-center justify-center gap-1">
                          {getChangeIcon(value.percentage_change)}
                          <span className={`text-lg font-bold ${getChangeColor(value.percentage_change)}`}>
                            {value.percentage_change > 0 ? "+" : ""}{value.percentage_change}%
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {value.difference > 0 ? "+" : ""}{value.difference}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {!comparison && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Calendar className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Select Periods to Compare</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  Choose two periods above and click "Compare" to see the differences
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
